import srraf from "srraf";
import { emit, on } from "@/neon/events";

const selectors = {
  headerContainer: ".header-container",
  headerWrapper: "#header",
  utilityBar: ".utility-bar-section",
  logoWrapper: ".header__logo-wrapper",
};

const stickyHeader = node => {
  if (!node) return;
  const root = document.documentElement;
  // Elements can change when resizing or altering header
  let headerParent = null;
  let headerHeight = null;
  let headerWrapper = null;
  let utilityBar = null;
  let utilityBarHeight = null;
  let logo = null;
  let headerHasCustomLogoImage = null;
  let initialHeightSet = false;

  let transparentEnabled = null;

  let scroller = null;
  let stickyScroller = null;

  // Breakpoint is equal to 60em
  const mediumBP = 960;
  const offsetRootVar = "--header-offset-height";
  const initialHeight = "--header-initial-height";

  // get elements & element heights
  const _defineElements = () => {
    headerParent = document.querySelector(selectors.headerContainer);
    headerWrapper = node.querySelector(selectors.headerWrapper);
    utilityBar = node.querySelector(selectors.utilityBar);
    utilityBarHeight = utilityBar ? utilityBar.offsetHeight : 0;
    headerHeight = utilityBarHeight + headerWrapper.offsetHeight;
    logo = node.querySelector(selectors.logoWrapper);
    headerHasCustomLogoImage = node.querySelector(".header__logo-image img");
    // True if the transparent header is enabled in the theme editor
    transparentEnabled = JSON.parse(headerWrapper.dataset.transparentHeader);
  };

  _defineElements();

  const _screenUnderMediumBP = () => {
    const viewportWidth =
      window.innerWidth || document.documentElement.clientWidth;

    return viewportWidth <= mediumBP;
  };

  let screenUnderMediumBP = _screenUnderMediumBP();

  scroller = srraf(({ vw }) => {
    _setRootVar(
      "--header-height",
      utilityBarHeight + headerWrapper.offsetHeight
    );

    const currentScreenWidthUnderMediumBP = vw <= mediumBP;

    if (currentScreenWidthUnderMediumBP !== screenUnderMediumBP) {
      screenUnderMediumBP = currentScreenWidthUnderMediumBP;
      _setupStickyHeader();
    }
  });

  const _setRootVar = (name, value) => {
    root.style.setProperty(name, `${value}px`);
  };

  const _setHeaderType = () => {
    // Remove header types incase of customizer changes
    root.classList.remove("header-transparent", "header-sticky");

    root.classList.add(
      transparentEnabled ? "header-transparent" : "header-sticky"
    );
  };

  const _setupStickyHeader = () => {
    //redifine elements & heights
    _defineElements();

    // If the header has a logo image we calculate values after image has loaded
    if (
      !headerHasCustomLogoImage ||
      (headerHasCustomLogoImage && headerHasCustomLogoImage.complete)
    ) {
      _processStickyHeader();
    } else {
      headerHasCustomLogoImage.addEventListener("load", _processStickyHeader, {
        once: true,
      });
    }
  };

  const _processStickyHeader = () => {
    if (!initialHeightSet) {
      _setRootVar(initialHeight, headerHeight);
      initialHeightSet = true;
    }

    _setRootVar(offsetRootVar, headerHeight);
    _setHeaderType();
    _initStickyHeader(_getStickyOffsets());
  };

  // Returns object of offset values
  const _getStickyOffsets = () => {
    const offsets = {};

    offsets.stickyHeaderResetPosition = utilityBarHeight ? 0 : 1;

    // Set offSets based on size and content of header
    offsets.offsetHeight = utilityBarHeight;
    offsets.scrollYToSticky = utilityBarHeight ? utilityBarHeight : 1;

    return offsets;
  };

  const _initStickyHeader = offsets => {
    const { scrollYToSticky, offsetHeight, stickyHeaderResetPosition } =
      offsets;

    // Destroy stickyScroller if one already exists
    if (stickyScroller) {
      stickyScroller.destroy();
    }

    // Init the scroller to monitor for y position to stick/unstick header
    stickyScroller = srraf(({ y }) => {
      if (!document.body.classList.contains("scroll-lock")) {
        if (y < scrollYToSticky) {
          _toggleStickyHeader(false, headerParent);
          if (utilityBarHeight) {
            _setElementTopPosition(headerParent, stickyHeaderResetPosition);
          }
          _setRootVar(offsetRootVar, headerHeight - y);
        } else if (y >= scrollYToSticky) {
          _toggleStickyHeader(true, headerParent);
          if (utilityBarHeight) {
            _setElementTopPosition(headerParent, -offsetHeight);
          }
          _setRootVar(offsetRootVar, headerHeight);
        }
      }
    });

    stickyScroller.update();
  };

  const _setElementTopPosition = (element, value) => {
    element.style.top = `${value}px`;
  };

  const _toggleStickyHeader = (isSticky, element) => {
    if (isSticky) {
      root.classList.add("header-stuck");
      element.classList.add("is-sticky");
      headerWrapper.classList.remove("header--transparent");
      emit("sticky-header:stuck");
    } else {
      element.classList.remove("is-sticky");
      root.classList.remove("header-stuck");

      if (transparentEnabled) {
        headerWrapper.classList.add("header--transparent");
      }
    }
  };

  const _reload = () => {
    scroller.update();
    _setupStickyHeader();

    // Let pages know the header changed
    emit("headerChange", () => {});
  };

  on("sticky-header:reload", () => {
    _reload();
  });

  _setupStickyHeader();

  const unload = () => {
    if (scroller) {
      scroller.destroy();
    }
    if (stickyScroller) {
      stickyScroller.destroy();
    }
  };

  return { unload };
};

export default stickyHeader;
